import React, { useState } from 'react';

function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Name is required';
    if (!formData.email.trim()) {
      errs.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errs.email = 'Invalid email address';
    }
    if (!formData.phone.trim()) {
      errs.phone = 'Phone number is required';
    } else if (!/^\(\d{3}\)\d{3}-\d{4}$/.test(formData.phone)) {
      errs.phone = 'Use format (XXX)XXX-XXXX';
    }
    if (!formData.message.trim()) errs.message = 'Message is required';
    return errs;
  };

  const handleChange = (e) => {
    setFormData({ 
      ...formData, 
      [e.target.name]: e.target.value 
    });
    setErrors((prev) => ({ ...prev, [e.target.name]: undefined }));
    setServerError(null);
    setRequestId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError(null);
    setRequestId(null);

    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSubmitting(true);

    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 20000); // 20s client timeout

    try {
      const resp = await fetch(
        'https://01s8aky6al.execute-api.us-east-2.amazonaws.com/dev/contact',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
          signal: ctrl.signal,
        }
      );

      clearTimeout(t);

      // Read requestId from header regardless of status
      const headerReqId = resp.headers.get('X-Request-Id') || null;

      let data = null;
      try {
        data = await resp.json();
      } catch {
        // In case the backend fails before JSON; we still have headerReqId
      }

      if (!resp.ok) {
        // Server-side structured errors
        const code = data?.code || `http_${resp.status}`;
        const rid = data?.requestId || headerReqId || 'unknown';

        // Field errors
        if (resp.status === 422 && data?.errors) {
          setErrors(data.errors);
        }

        setServerError(
          `Request failed (${code}). ${data?.ses?.name ? `SES: ${data.ses.name}` : ''}`.trim()
        );
        setRequestId(rid);
        return;
      }

      window.alert('Email sent successfully!');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      clearTimeout(t);
      // Network/timeout errors
      if (err.name === 'AbortError') {
        setServerError('Request timed out. Please try again.');
      } else {
        setServerError(`Network error: ${err.message}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-custom-green py-10 px-5 md:px-10">
      <h2 className="text-center text-black text-3xl font-bold mb-6">CONTACT US</h2>
      <form
        onSubmit={handleSubmit}
        className="max-w-4xl mx-auto bg-white p-5 rounded-md shadow"
      >
        {serverError && (
          <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700">
            <p className="font-semibold">We couldn’t send your message.</p>
            <p className="text-sm">{serverError}</p>
            {requestId && (
              <p className="text-xs mt-1 text-gray-600">
                Request ID: <span className="font-mono">{requestId}</span>
              </p>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-5 mb-5">
          {/* Left column */}
          <div className="space-y-3">
            <div>
              <input
                name="name"
                value={formData.name}
                onChange={handleChange}
                type="text"
                placeholder="Enter your name (Required)"
                className="bg-white w-full border-2 border-custom-darkGreen p-2 rounded text-black"
                aria-invalid={!!errors.name}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>
            <div>
              <input
                name="email"
                value={formData.email}
                onChange={handleChange}
                type="email"
                placeholder="Enter email (Required)"
                className="bg-white w-full border-2 border-custom-darkGreen p-2 rounded text-black"
                aria-invalid={!!errors.email}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>
            <div>
              <input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                type="tel"
                placeholder="(XXX)XXX-XXXX (Required)"
                className="bg-white w-full border-2 border-custom-darkGreen p-2 rounded text-black"
                aria-invalid={!!errors.phone}
              />
              {errors.phone && (
                <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
              )}
            </div>
          </div>

          {/* Right column */}
          <div className="space-y-3">
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Message (Required)"
              className="bg-white w-full border-2 border-custom-darkGreen p-2 rounded text-black"
              style={{ minHeight: '138px' }}
              aria-invalid={!!errors.message}
            />
            {errors.message && (
              <p className="text-red-500 text-sm mt-1">{errors.message}</p>
            )}
          </div>
        </div>

        <p className="text-sm text-center text-gray-600 mb-5">
          Please do not submit any Protected Health Information (PHI).
        </p>

        <div className="text-center">
          <button
            type="submit"
            disabled={submitting}
            className="bg-custom-regularGreen text-black px-6 py-2 rounded shadow hover:bg-custom-darkGreen transition-colors disabled:opacity-50"
          >
            {submitting ? 'Sending…' : 'SUBMIT'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ContactUs;
